import type { FC } from 'react'
import type { HistoryItem, SceneProps } from '@/components/landing/games/scene-ui'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useSequence } from '@/hooks/use-sequence'
import CountUp from '@/components/landing/count-up'
import { HistoryChip, SceneBadge, SceneField } from '@/components/landing/games/scene-ui'

const TARGET = 50
const PAYOUT = 1.98
const TICKS = [0, 25, 50, 75, 100]

const POSTER_HISTORY: HistoryItem[] = [
    { id: 4, value: 12.07 },
    { id: 3, value: 88.31 },
    { id: 2, value: 49.12 },
    { id: 1, value: 63.9 }
]

const DiceScene: FC<SceneProps> = ({ active }) => {
    const [value, setValue] = useState(73.42)
    const [history, setHistory] = useState<HistoryItem[]>(POSTER_HISTORY)
    const nextId = useRef(5)

    useSequence(active, async ({ wait }) => {
        const roll = Math.floor(Math.random() * 10001) / 100
        setValue(roll)

        if (!(await wait(950))) return

        const id = nextId.current
        nextId.current += 1
        setHistory(previous => [{ id, value: roll }, ...previous].slice(0, 5))

        await wait(1250)
    })

    const win = value > TARGET

    return (
        <div className='absolute inset-0 flex flex-col justify-center px-6 pb-6 pt-14 sm:px-8'>
            <div className='flex items-end justify-between gap-4'>
                <CountUp
                    value={value}
                    duration={750}
                    className={cn('font-mono text-[44px] font-medium leading-none tabular-nums tracking-tight transition-colors duration-300 sm:text-5xl', win ? 'ember-glow text-ember' : 'text-bone/40')}
                />
                <SceneBadge tone={win ? 'ember' : 'muted'}>
                    {win ? `Win ×${PAYOUT.toFixed(2)}` : 'No win'}
                </SceneBadge>
            </div>

            <div className='mt-3 flex h-5 gap-1 overflow-hidden'>
                {history.map(item => (
                    <HistoryChip key={item.id} hot={item.value > TARGET}>
                        {item.value.toFixed(2)}
                    </HistoryChip>
                ))}
            </div>

            <div className='relative mt-6'>
                <div className='flex h-2.5 overflow-hidden rounded-full'>
                    <div className='h-full bg-white/[0.08]' style={{ width: `${TARGET}%` }} />
                    <div className='h-full flex-1 bg-gradient-to-r from-ember/60 to-ember' />
                </div>

                <div aria-hidden='true' className='absolute -bottom-2 -top-2 w-px bg-bone/50' style={{ left: `${TARGET}%` }} />

                <div
                    aria-hidden='true'
                    className='absolute top-1/2 -translate-x-1/2 -translate-y-1/2 transition-[left] duration-700 ease-[cubic-bezier(0.2,0.9,0.25,1.12)]'
                    style={{ left: `${value}%` }}
                >
                    <div className='grid size-8 place-items-center rounded-[9px] border border-white/30 bg-bone shadow-[0_10px_28px_-8px_rgba(0,0,0,0.95)]'>
                        <div className='grid grid-cols-2 gap-1'>
                            <span className='size-1.5 rounded-full bg-ink' />
                            <span className='size-1.5 rounded-full bg-ink' />
                            <span className='size-1.5 rounded-full bg-ink' />
                            <span className='size-1.5 rounded-full bg-ink' />
                        </div>
                    </div>
                </div>
            </div>

            <div className='mt-3 flex justify-between font-mono text-[10px] text-steel/70'>
                {TICKS.map(tick => (
                    <span key={tick}>{tick}</span>
                ))}
            </div>

            <div className='mt-5 grid grid-cols-3 gap-2'>
                <SceneField label='Roll over' value='50.00' />
                <SceneField label='Payout' value={`×${PAYOUT.toFixed(2)}`} />
                <SceneField label='Win chance' value='50.00%' />
            </div>
        </div>
    )
}

export default DiceScene
